import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { usePlaylist } from "../../../contexts/PlaylistContext";
import useLoadTracksWithPreviews from "../../../hooks/useLoadTracksWithPreviews";
import { getOptimizedImageUrl, trackClick } from "../../_helpers";
import BaseModal from "../../ui/BaseModal";

const playlistSurface = "#17130f";
const playlistPanel = "#241f19";
const playlistText = "#f3eadc";
const playlistMuted = "#bda989";
const playlistAccent = "var(--logoColor2)";

const PlaylistPanel = () => {
  const {
    playlist,
    currentTrack,
    currentTrackIndex,
    isPlaying,
    isPanelOpen,
    albums,
    artistName,
    playTrack,
    togglePlayPause,
    removeFromPlaylist,
    clearPlaylist,
    togglePanel,
    enqueueTracks,
  } = usePlaylist();

  const { loadTracks, isLoading, loadedCount } = useLoadTracksWithPreviews();

  const handleLoadMore = async () => {
    trackClick("playlist-load-more", { label: "Load more tracks", section: "playlist-panel", artist: artistName });
    if (!albums || !artistName || isLoading) return;

    await loadTracks({
      albums,
      artistName,
      limit: 10,
      onTrackLoaded: (trackData) => {
        enqueueTracks([trackData]);
      },
    });
  };

  const handleClosePanel = () => {
    if (isPanelOpen) togglePanel();
  };

  return (
    <BaseModal
      isOpen={isPanelOpen}
      onClose={handleClosePanel}
      desktopModalMobileDrawer
      drawerDirection="right"
      drawerWidth="min(100vw, max(50vw, 42rem))"
      hideCloseButton
      noPadding
      showHandle={false}
      bodySx={{
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        height: "100%",
        maxHeight: "100dvh",
        backgroundColor: playlistSurface,
        borderLeft: { xs: "none", md: `0.125rem solid ${playlistAccent}` },
        borderRadius: 0,
        boxShadow: "-0.25rem 0 1.25rem rgba(0,0,0,0.3)",
        color: playlistText,
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            padding: { xs: "1rem", md: "1.35rem" },
            borderBottom: `0.125rem solid ${playlistAccent}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: playlistPanel,
            gap: "1rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.65rem", md: "1rem" }, minWidth: 0 }}>
            <PlaylistPlayIcon
              sx={{ color: playlistAccent, fontSize: { xs: "30px", md: "1.75rem" }, flexShrink: 0 }}
            />
            <Typography
              sx={{
                color: playlistText,
                fontFamily: "Hanson",
                fontSize: { xs: "24px", md: "1.75rem" },
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              PLAYLIST
            </Typography>
            <Typography
              sx={{
                color: playlistSurface,
                fontFamily: "Poppins",
                fontSize: { xs: "16px", md: "1rem" },
                fontWeight: 600,
                backgroundColor: playlistAccent,
                padding: { xs: "4px 8px", md: "0.3rem 0.6rem" },
                borderRadius: "0.25rem",
                flexShrink: 0,
              }}
            >
              {playlist.length}
              {/* {playlist.length === 1 ? "track" : "tracks"}) */}
            </Typography>
          </Box>

          <IconButton
            onClick={() => {
              trackClick("playlist-close", { label: "Close playlist", section: "playlist-panel" });
              togglePanel();
            }}
            sx={{
              color: playlistText,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: "30px", md: "1.75rem" } }} />
          </IconButton>
        </Box>

        {/* Playlist Items */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: { xs: "0.75rem", md: "1rem" },
            "&::-webkit-scrollbar": {
              width: "0.5rem",
            },
            "&::-webkit-scrollbar-track": {
              background: playlistPanel,
            },
            "&::-webkit-scrollbar-thumb": {
              background: playlistAccent,
              borderRadius: "0.25rem",
            },
          }}
        >
          {playlist.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: "1.5rem",
              }}
            >
              <PlaylistPlayIcon
                sx={{
                  color: playlistMuted,
                  fontSize: { xs: "48px", md: "4.5rem" },
                  opacity: 0.3,
                }}
              />
              <Typography
                sx={{
                  color: playlistMuted,
                  fontFamily: "Poppins",
                  fontSize: { xs: "17px", md: "1.25rem" },
                  textAlign: "center",
                }}
              >
                Your playlist is empty
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: { xs: "1.25rem", md: "1rem" } }}
            >
              {playlist.map((track, index) => {
                const isCurrentTrack = index === currentTrackIndex;
                return (
                  <Box
                    key={`${track.audioUrl}-${index}`}
                    onClick={() => {
                      trackClick("playlist-track-play", { label: "Playlist track", section: "playlist-panel", artist: track.artist, track: track.title, trackId: track.id });
                      playTrack(index);
                    }}
                    sx={{
                      display: "flex",
                      gap: { xs: "0.75rem", md: "1.25rem" },
                      padding: { xs: "0.75rem", md: "1rem" },
                      backgroundColor: isCurrentTrack ? playlistPanel : "rgba(255,255,255,0.035)",
                      border: isCurrentTrack
                        ? `0.125rem solid ${playlistAccent}`
                        : "0.125rem solid transparent",
                      borderRadius: "0.5rem",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: playlistPanel,
                        transform: "translateX(-4px)",
                      },
                    }}
                  >
                    {/* Artwork */}
                    {track.artwork && (
                      <Box
                        component="img"
                        src={getOptimizedImageUrl(track.artwork, { width: 112, quality: 80 })}
                        alt={track.title}
                        sx={{
                          width: { xs: "64px", md: "3.5rem" },
                          height: { xs: "64px", md: "3.5rem" },
                          borderRadius: "0.25rem",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Track Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: isCurrentTrack ? playlistAccent : playlistText,
                          fontFamily: "Poppins",
                          fontSize: { xs: "18px", md: "1.275rem" },
                          lineHeight: 1.15,
                          fontWeight: isCurrentTrack ? 700 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {track.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: playlistMuted,
                          fontFamily: "Poppins",
                          fontSize: { xs: "13px", md: "1rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          opacity: 0.8,
                        }}
                      >
                        {track.artist}
                      </Typography>
                    </Box>

                    {/* Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      {isCurrentTrack && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            trackClick(isPlaying ? "playlist-pause" : "playlist-resume", { label: "Playlist play toggle", section: "playlist-panel", artist: track.artist, track: track.title, trackId: track.id });
                            togglePlayPause();
                          }}
                          sx={{
                            color: playlistAccent,
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          {isPlaying ? (
                            <PauseIcon sx={{ fontSize: { xs: "3rem", md: "1.75rem" } }} />
                          ) : (
                            <PlayArrowIcon sx={{ fontSize: { xs: "3rem", md: "1.75rem" } }} />
                          )}
                        </IconButton>
                      )}

                      <IconButton
                        aria-label={`Remove ${track.title} from playlist`}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          trackClick("playlist-remove-track", { label: "Remove track", section: "playlist-panel", artist: track.artist, track: track.title, trackId: track.id });
                          removeFromPlaylist(index);
                        }}
                        sx={{
                          width: { xs: "34px", md: "2rem" },
                          height: { xs: "34px", md: "2rem" },
                          color: playlistText,
                          border: `1px solid ${playlistMuted}`,
                          borderRadius: "999px",
                          opacity: 0.8,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: playlistAccent,
                            borderColor: playlistAccent,
                            backgroundColor: "rgba(255,255,255,0.08)",
                            opacity: 1,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: { xs: "18px", md: "1.175rem" } }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer with Load More and Clear All */}
        {playlist.length > 0 && (
          <Box
            sx={{
              padding: { xs: "1.25rem", md: "1rem" },
              borderTop: `0.125rem solid ${playlistAccent}`,
              backgroundColor: playlistPanel,
              display: "flex",
              flexDirection: "column",
              gap: { xs: "1rem", md: "0.75rem" },
            }}
          >
            {/* Load More Button */}
            {albums && artistName && (
              <Button
                onClick={handleLoadMore}
                disabled={isLoading}
                fullWidth
                sx={{
                  width: "100%",
                  color: playlistText,
                  fontFamily: "Hanson",
                  fontSize: { xs: "15px", md: ".84rem" },
                  padding: { xs: "0.75rem", md: "0.75rem" },
                  borderRadius: "0.5rem",
                  border: `0.0625rem solid ${playlistAccent}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "scale(1.02)",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: playlistAccent }} />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>Load 10 More Tracks</>
                )}
              </Button>
            )}

            {/* Clear All Button */}
            <Button
              onClick={() => {
                trackClick("playlist-clear", { label: "Clear playlist", section: "playlist-panel" });
                clearPlaylist();
              }}
              fullWidth
              sx={{
                width: "100%",
                color: playlistText,
                fontFamily: "Hanson",
                fontSize: { xs: "15px", md: ".84rem" },
                padding: { xs: "0.75rem", md: "0.75rem" },
                borderRadius: "0.5rem",
                border: `0.0625rem solid ${playlistAccent}`,
                "&:hover": {
                  backgroundColor: "rgba(255,68,68,0.1)",
                  borderColor: "#ff4444",
                  color: "#ff4444",
                },
              }}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default PlaylistPanel;
